import api, { handleResponse, handleError } from './api';
import { UPLOAD_BASE } from '../utils/apiBase';

/**
 * Event media gallery — additional photos + embed videos beyond the poster.
 *
 *   EventMedia {
 *     id, eventId, sortOrder, kind ('image'|'embed'|'video'),
 *     url, embedProvider, embedId, embedUrl, hlsUrl, dashUrl, status,
 *     thumbnailUrl, width, height, durationSec
 *   }
 */
const eventMediaService = {
  async list(eventId) {
    try { return handleResponse(await api.get(`/events/${eventId}/media`)); }
    catch (e) { return handleError(e); }
  },

  async addImage(eventId, file) {
    try {
      const form = new FormData();
      form.append('file', file);
      return handleResponse(await api.post(`/events/${eventId}/media/image`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }));
    } catch (e) { return handleError(e); }
  },

  async addEmbed(eventId, url) {
    try { return handleResponse(await api.post(`/events/${eventId}/media/embed`, { url })); }
    catch (e) { return handleError(e); }
  },

  async reorder(eventId, orderedIds) {
    try { return handleResponse(await api.put(`/events/${eventId}/media/reorder`, { orderedIds })); }
    catch (e) { return handleError(e); }
  },

  async remove(mediaId) {
    try { return handleResponse(await api.delete(`/media/${mediaId}`)); }
    catch (e) { return handleError(e); }
  },

  /** { videoUpload: bool } — whether host video upload is configured in this env. */
  async capabilities() {
    try { return handleResponse(await api.get('/media/capabilities')); }
    catch (e) { return handleError(e); }
  },

  /**
   * Upload a video through our server (which forwards to Cloudflare). Avoids the
   * browser→Cloudflare TLS failures some networks hit (ERR_SSL_BAD_RECORD_MAC).
   * Returns the pending media row; poll refresh() until it's ready.
   */
  async uploadVideo(eventId, file, onProgress) {
    try {
      const form = new FormData();
      form.append('file', file);
      return handleResponse(await api.post(`/events/${eventId}/media/video`, form, {
        baseURL: UPLOAD_BASE,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: onProgress
          ? (e) => onProgress(e.total ? Math.round((e.loaded / e.total) * 100) : 0)
          : undefined,
        timeout: 15 * 60 * 1000,
      }));
    } catch (e) { return handleError(e); }
  },

  /** Poll a pending uploaded video until Cloudflare finishes encoding. */
  async refresh(mediaId) {
    try { return handleResponse(await api.post(`/media/${mediaId}/refresh`)); }
    catch (e) { return handleError(e); }
  },

  /** Pick the poster frame at a 0..1 fraction of an uploaded video. */
  async setThumbnail(mediaId, pct) {
    try { return handleResponse(await api.post(`/media/${mediaId}/thumbnail`, { pct })); }
    catch (e) { return handleError(e); }
  },

  /**
   * Upload the file straight to Cloudflare's one-time URL (browser → Cloudflare, not
   * through our API). Cloudflare direct-creator-upload accepts a multipart POST with a
   * "file" field. Returns { ok, error }.
   */
  async uploadToCloudflare(uploadUrl, file) {
    try {
      const form = new FormData();
      form.append('file', file);
      const resp = await fetch(uploadUrl, { method: 'POST', body: form });
      return { ok: resp.ok, error: resp.ok ? null : `Upload failed (${resp.status})` };
    } catch (e) {
      return { ok: false, error: e.message || 'Upload failed' };
    }
  },
};

export default eventMediaService;
